import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useStripeConnect } from "@/hooks/use-stripe-connect";
import {
	AlertCircle,
	BanknoteIcon,
	CheckCircle,
	RefreshCw,
} from "lucide-react";
import { useEffect, useState } from "react";

export function PaymentsSection() {
	const { isSupplier } = useAuth();
	const { toast } = useToast();
	const {
		isLoading,
		account,
		accountLink,
		getAccountStatus,
		createConnectAccount,
		refreshAccountLink,
	} = useStripeConnect();
	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		const initializeAccount = async () => {
			if (isSupplier) {
				await getAccountStatus();
			}
			setIsInitialized(true);
		};

		initializeAccount();
	}, [isSupplier]);

	const handleCreateAccount = async () => {
		const success = await createConnectAccount();
		if (success && accountLink) {
			window.location.href = accountLink;
		}
	};

	const handleRefreshAccount = async () => {
		await refreshAccountLink();
	};

	// If the page was loaded with success parameter
	useEffect(() => {
		const queryParams = new URLSearchParams(window.location.search);
		if (queryParams.get("success") === "true") {
			toast({
				title: "Account setup successful",
				description:
					"Your Stripe account has been successfully connected.",
			});

			// Refresh account status after successful onboarding
			getAccountStatus();

			// Clean up URL parameters
			window.history.replaceState(null, "", window.location.pathname);
		}

		if (queryParams.get("refresh") === "true") {
			// Refresh account status when returning from refresh URL
			getAccountStatus();

			// Clean up URL parameters
			window.history.replaceState(null, "", window.location.pathname);
		}
	}, []);

	if (!isInitialized) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-center p-8">
					<div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="space-y-6">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center">
							<BanknoteIcon className="mr-2 h-5 w-5" />
							Stripe Connect Account
						</CardTitle>
						<CardDescription>
							Connect your Stripe account to receive payments from
							customers through the Starch marketplace.
						</CardDescription>
					</CardHeader>
					<CardContent>
						{account ? (
							<div className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<div className="bg-gray-50 p-4 rounded-lg">
										<div className="text-sm text-gray-500 mb-1">
											Account Status
										</div>
										<div className="flex items-center">
											{account.detailsSubmitted ? (
												<CheckCircle className="h-5 w-5 text-green-500 mr-2" />
											) : (
												<AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
											)}
											<span className="font-medium">
												{account.detailsSubmitted
													? "Details Submitted"
													: "Incomplete"}
											</span>
										</div>
									</div>
									<div className="bg-gray-50 p-4 rounded-lg">
										<div className="text-sm text-gray-500 mb-1">
											Charges Enabled
										</div>
										<div className="flex items-center">
											{account.chargesEnabled ? (
												<CheckCircle className="h-5 w-5 text-green-500 mr-2" />
											) : (
												<AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
											)}
											<span className="font-medium">
												{account.chargesEnabled
													? "Enabled"
													: "Disabled"}
											</span>
										</div>
									</div>
									<div className="bg-gray-50 p-4 rounded-lg">
										<div className="text-sm text-gray-500 mb-1">
											Payouts Enabled
										</div>
										<div className="flex items-center">
											{account.payoutsEnabled ? (
												<CheckCircle className="h-5 w-5 text-green-500 mr-2" />
											) : (
												<AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
											)}
											<span className="font-medium">
												{account.payoutsEnabled
													? "Enabled"
													: "Disabled"}
											</span>
										</div>
									</div>
								</div>

								{!account.detailsSubmitted && (
									<Alert className="bg-amber-50 text-amber-900 border-amber-200">
										<AlertCircle className="h-4 w-4 text-amber-600" />
										<AlertTitle>Action Required</AlertTitle>
										<AlertDescription>
											Your Stripe account setup is
											incomplete. Please complete the
											onboarding process to receive
											payments.
										</AlertDescription>
									</Alert>
								)}
							</div>
						) : (
							<Alert>
								<AlertCircle className="h-4 w-4" />
								<AlertTitle>No Stripe Account</AlertTitle>
								<AlertDescription>
									You don't have a Stripe account connected
									yet. Create one to receive payments.
								</AlertDescription>
							</Alert>
						)}
					</CardContent>
					<CardFooter className="flex justify-end gap-4">
						{account ? (
							<Button
								variant="accent"
								className="gap-2"
								onClick={handleRefreshAccount}
								disabled={isLoading}>
								{isLoading ? (
									<RefreshCw className="h-4 w-4 animate-spin" />
								) : (
									<RefreshCw className="h-4 w-4" />
								)}
								Update Account
							</Button>
						) : (
							<Button
								className="gap-2"
								onClick={handleCreateAccount}
								disabled={isLoading}>
								{isLoading ? (
									<RefreshCw className="h-4 w-4 animate-spin" />
								) : (
									<BanknoteIcon className="h-4 w-4" />
								)}
								Connect with Stripe
							</Button>
						)}
					</CardFooter>
				</Card>
			</div>
		</div>
	);
}
